/* eslint-disable @typescript-eslint/no-explicit-any */

import type { AxiosError, AxiosInstance } from "axios";
import { toast } from "react-toastify";
import type { AuthModel } from "./_models";
import { API_KEY, encrypt } from "./crypto";

const AUTH_LOCAL_STORAGE_KEY = "kt-auth-react-v";

// const queryClient = useQueryClient();
const getAuth = (): AuthModel | undefined => {
	if (!sessionStorage) {
		return;
	}
	const lsValue: string | null = localStorage.getItem(AUTH_LOCAL_STORAGE_KEY);

	if (!lsValue) {
		return;
	}

	try {
		const auth: AuthModel = JSON.parse(lsValue) as AuthModel;
		if (auth) {
			const token = auth.access_token || auth.token;
			if (!token) return;

			const payload = token.split(".")[1];
			const decodedPayload = JSON.parse(atob(payload));
			const addRoleId = {
				...auth,
				role_id: decodedPayload.role_id,
				access_token: token,
			};
			return addRoleId;
		}
	} catch (error) {
		console.error("AUTH LOCAL STORAGE PARSE ERROR", error);
	}
};

const setAuth = (auth: AuthModel) => {
	if (!sessionStorage) {
		return;
	}

	try {
		const lsValue = JSON.stringify(auth);
		localStorage.setItem(AUTH_LOCAL_STORAGE_KEY, lsValue);
	} catch (error) {
		console.error("AUTH LOCAL STORAGE SAVE ERROR", error);
	}
};

const removeAuth = () => {
	if (!sessionStorage) {
		return;
	}

	try {
		localStorage.removeItem(AUTH_LOCAL_STORAGE_KEY);
	} catch (error) {
		console.error("AUTH LOCAL STORAGE REMOVE ERROR", error);
	}
};

export async function setupAxios(axiosInstance: AxiosInstance): Promise<void> {
	// const encryptedApiKeyPromise = encrypt(API_KEY, "apikey");
	const encryptedApiKeyPromise = API_KEY;

	// Axios defaults
	axiosInstance.defaults.baseURL = import.meta.env.VITE_APP_API_URL || "";
	axiosInstance.defaults.timeout = 10500;
	axiosInstance.defaults.headers.common = {
		Accept: "application/json",
		"Content-Type": "application/json",
		"X-Requested-With": "XMLHttpRequest",
	};
	axiosInstance.defaults.withCredentials = true; // refresh token in cookie

	const _isRefreshing = false;
	let failedQueue: any[] = [];

	const _processQueue = (error: any, token: string | null = null) => {
		failedQueue.forEach((prom) => {
			if (error) prom.reject(error);
			else prom.resolve(token);
		});
		failedQueue = [];
	};

	// Request interceptor
	axiosInstance.interceptors.request.use(
		async (config: any) => {
			const auth = getAuth();
			if (!config.headers) config.headers = {};

			if (!config.headers.Authorization) {
				const encryptedApiKey = await encryptedApiKeyPromise;
				const token = auth?.access_token || auth?.token;
				config.headers.Authorization = token ? `${token}` : encryptedApiKey;

				if (!token) {
					config.headers["App-Key"] = encryptedApiKey;
					config.headers["Authorization"] = encryptedApiKey;
				}
			}
			return config;
		},
		(error: AxiosError) => {
			toast.error("Request error: Unable to send request.");
			return Promise.reject(error);
		},
	);

	// Response interceptor
	axiosInstance.interceptors.response.use(
		(response) => response,
		async (error: AxiosError & { config?: any }) => {
			const originalRequest = error.config;
			const status = error.response?.status;
			const _message =
				(error.response?.data as { message?: string })?.message ||
				error.message ||
				"Something went wrong";

			if (status === 401 && !originalRequest._retry) {
			}

			// Other errors
			switch (status) {
				case 401:
					toast.error("Unauthorized. Please log in.", {
						toastId: "unauthorized",
					});
					localStorage.removeItem(AUTH_LOCAL_STORAGE_KEY);
					window.location.href = "/auth/login";
					break;
				case 403:
					toast.error("Forbidden. You don't have permission.", {
						toastId: "forbidden-access",
					});
					// localStorage.removeItem(AUTH_LOCAL_STORAGE_KEY);
					// window.location.href = "/auth/login";
					break;
				case 419:
					toast.error("Session expired. Authentication Timeout.", {
						toastId: "session-expired",
					});
					break;
				case 429:
					toast.error("Too Many Requests. Please try again later.", {
						toastId: "too-many-requests",
					});
					break;
				// Removed default case and case 500 to prevent duplicate error messages
				// Individual API calls handle their own error messages
			}

			return Promise.reject(error);
		},
	);
}

export { AUTH_LOCAL_STORAGE_KEY, getAuth, removeAuth, setAuth };
